import { Delete, ExpandMore, SearchOff } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { format, formatISO, isValid, parse, parseISO } from 'date-fns';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import EnhancedTableHead from '@sophocles/components/EnhancedTable/EnhancedTableHead';
import EnhancedTableToolbar from '@sophocles/components/EnhancedTable/EnhancedTableToolbar';
import { addToast } from '@sophocles/features/toast';
import { getComparator, parseFiltersToActive, stableSort } from '@sophocles/utils/table';

function DynamicRow({
  children,
  row,
  rowDetailRenderer,
  rowDetailColumns,
  order,
  orderBy,
  onRequestSort,
  ...rowProps
}) {
  const [open, setOpen] = useState(false);

  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} {...rowProps}>
        {rowDetailRenderer && (
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
        )}
        {children}
      </TableRow>
      {rowDetailRenderer && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table size="small">
                {rowDetailColumns && (
                  <TableHead>
                    <TableRow>
                      {rowDetailColumns.map((headCell, index) => {
                        const orderByKey = headCell.param ? headCell.param : headCell.id;
                        return (
                          <TableCell
                            key={`dynamic-row-${orderByKey}-${index}`}
                            variant="head"
                            align={headCell.align ?? 'left'}
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === orderByKey ? order : false}
                            style={headCell.style ?? {}}
                          >
                            {headCell.sortable || false ? (
                              <TableSortLabel
                                active={orderBy === orderByKey}
                                direction={orderBy === orderByKey ? order : 'asc'}
                                onClick={createSortHandler(orderByKey)}
                              >
                                {headCell.label}
                                {orderBy === orderByKey ? (
                                  <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                  </Box>
                                ) : null}
                              </TableSortLabel>
                            ) : (
                              <span
                                style={{
                                  maxWidth: '100%',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                }}
                              >
                                {headCell.label}
                              </span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                )}
                {rowDetailRenderer(row)}
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function DynamicTable(
  {
    title,
    rows,
    columns,
    groupedColumns,
    editable = false,
    selectable = false,
    clickable = false,
    hoverable = false,
    searchable = false,
    deletable = false,
    dateRange = false,
    expandable = false,
    showCheckBoxColumn = true,
    allowsRefresh = true,
    editIcon = <EditIcon />,
    grouping,
    performGrouping,
    useISOFormat = false,
    fetchData,
    filters = [],
    currentPage = null,
    initialPage,
    initialSearch,
    initialSelected,
    searchFields,
    defaultOrder = 'asc',
    defaultOrderBy,
    defaultRowsPerPage = 10,
    searchParam = 'text_search',
    getDetailRow = null,
    addNewElement,
    actions,
    footerRow,
    startDate,
    endDate,
    dateKeyword,
    onRowClick,
    onEdit,
    onDelete,
    onPageChange,
    onSelectedChange,
    onSortChange,
    onSearch,
    onRowsPerPageChange,
    onDateRangeChange,
    onActiveFiltersChange,
    onDataChange,
    iconButton = null,
    rowsPerPageOptions = [5, 10, 25, 50, 100],
    ...tableProps
  },
  ref,
) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [data, setData] = useState(rows ?? []);
  const [totalCount, setTotalCount] = useState(data ? data.length : 0);
  const [isFetching, setIsFetching] = useState(false);
  const [selected, setSelected] = useState(initialSelected ?? []);
  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [page, setPage] = useState(initialPage ?? currentPage ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage ?? 10);
  const [search, setSearch] = useState(initialSearch ?? '');
  const [activeFilters, setActiveFilters] = useState(parseFiltersToActive(filters));
  const [isInitialState, setIsInitialState] = useState(!!fetchData);
  const [expandedState, setExpandedState] = useState(rows != null ? new Array(rows.length).fill(false) : []);

  const isFetchRequired = !!fetchData;
  const isDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));

  useEffect(() => {
    if (page != currentPage) {
      setPage(currentPage);
    }
  }, [currentPage]);

  const formatSearchQuery = text => {
    return text === '' || !text ? '' : text.split(' ').join(',');
  };

  const getSelected = () => selected;

  const getData = () => data;

  const loadData = () => {
    setIsFetching(true);
    setSelected(initialSelected ?? []);

    const offset = page * rowsPerPage;

    const fetchProps = {
      limit: rowsPerPage,
      offset: offset,
    };

    if (dateRange) {
      fetchProps.dateRange = {
        field: dateKeyword,
        start: isValid(startDate) ? format(startDate, 'yyyy-MM-dd') : startDate,
        end: isValid(endDate) ? format(endDate, 'yyyy-MM-dd') : endDate,
      };
      let potentialDate;
      if (useISOFormat) {
        if (!isValid(fetchProps.dateRange.start)) {
          potentialDate = parse(fetchProps.dateRange.start, 'yyyy-MM-dd', new Date());
          if (!isValid(potentialDate)) {
            potentialDate = parseISO(fetchProps.dateRange.start);
          }
          fetchProps.dateRange.start = potentialDate;
        }
        if (!isValid(fetchProps.dateRange.end)) {
          potentialDate = parse(fetchProps.dateRange.end, 'yyyy-MM-dd', new Date());
          if (!isValid(potentialDate)) {
            potentialDate = parseISO(fetchProps.dateRange.end);
          }
          fetchProps.dateRange.end = potentialDate;
        }
        fetchProps.dateRange.start = formatISO(fetchProps.dateRange.start);
        fetchProps.dateRange.end = formatISO(fetchProps.dateRange.end);
      }
    }

    if (orderBy && order) {
      fetchProps.o = (order === 'desc' ? '-' : '') + orderBy;
    }

    if (searchable && search) {
      //searchFields.join(',') + '|' + search
      if (searchFields.length === 0) {
        fetchProps[searchParam] = formatSearchQuery(search);
      } else {
        fetchProps[searchParam] = searchFields.join(',') + '|' + formatSearchQuery(search);
      }
    }

    activeFilters.forEach(f => {
      if (filters[f.index] === undefined) {
        return;
      }

      const filterDefinition = filters[f.index];

      let urlKeyword = filterDefinition.id;

      const param = _.get(filterDefinition, 'param', null);
      if (param != null) {
        urlKeyword = param;
      }

      fetchProps[urlKeyword] = filterDefinition.getParamValue ? filterDefinition.getParamValue(f.value) : f.value;

      if (fetchProps[urlKeyword] == null) {
        delete fetchProps[urlKeyword];
      }
    });

    return fetchData(fetchProps)
      .then(res => {
        if (_.has(res, ['payload', 'meta', 'total_count'])) {
          setTotalCount(res.payload.meta.total_count);
        } else if (_.has(res, ['meta', 'total_count'])) {
          setTotalCount(res.meta.total_count);
        } else if (_.has(res, 'count')) {
          setTotalCount(res.count);
        } else if (_.has(res, ['payload', 'count'])) {
          setTotalCount(res.payload.count);
        }

        if (_.has(res, 'data')) {
          setData(res.data);
        } else if (_.has(res, ['payload', 'data'])) {
          setData(res.payload.data);
        } else if (_.has(res, ['payload', 'results'])) {
          setData(res.payload.results);
        } else if (_.has(res, 'results')) {
          setData(res.results);
        } else if (Array.isArray(res)) {
          setData(res);
        } else {
          setData([]);
          setTotalCount(0);
          dispatch(addToast({ message: 'Failed to fetch table data', level: 'error' }));
        }

        return data;
      })
      .catch(err => {
        console.error(err);
        dispatch(addToast({ message: 'Failed to fetch table data', level: 'error' }));
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const handleSearch = value => {
    setSearch(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const newSelecteds = data.map(n => n.id);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event, id, isCheckbox) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (!isCheckbox) {
      newSelected = [id];
    } else {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
      }
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = ids => {
    onDelete(ids);
  };

  const handleApplyFilters = newFilters => {
    setActiveFilters(newFilters);
    setPage(0);
  };

  useEffect(() => {
    if (search != null && !isInitialState) {
      if (isFetchRequired) {
        loadData();
      }
      if (onSearch) {
        onSearch(search);
      }
    }
  }, [search]);

  useImperativeHandle(ref, () => ({
    getData,
    loadData,
    getSelected,
    isLoading: isFetching,
  }));

  useEffect(() => {
    setExpandedState(data != null ? new Array(data.length).fill(false) : []);
    if (onDataChange) {
      onDataChange(data);
    }
  }, [data]);

  useEffect(() => {
    if (isFetchRequired) {
      loadData();
    }
    if (isInitialState) {
      setIsInitialState(false);
    }
  }, [page, rowsPerPage, order, orderBy, startDate, endDate, activeFilters]);

  useEffect(() => {
    if (onActiveFiltersChange) {
      onActiveFiltersChange(activeFilters);
    }
  }, [activeFilters]);

  useEffect(() => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(rowsPerPage);
    }
  }, [rowsPerPage]);

  useEffect(() => {
    if (onPageChange && !isInitialState) {
      onPageChange(page);
    }
  }, [page]);

  useEffect(() => {
    if (onSortChange && !isInitialState) {
      onSortChange(order, orderBy);
    }
  }, [order, orderBy]);

  useEffect(() => {
    if (onSelectedChange) {
      onSelectedChange(selected);
    }
  }, [selected]);

  const isSelected = id => selected.indexOf(id) !== -1;
  const isGrouped = performGrouping && grouping;

  const rawTableData = isFetchRequired
    ? data
    : stableSort(data, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  let tableData = rawTableData;
  if (isGrouped) {
    tableData = performGrouping(grouping, tableData);
  }

  const toggleExpandRow = rowIndex => {
    // Create a new array by mapping over the current state.
    const newExpandedState = expandedState.map((item, index) => {
      // Toggle the value at the specified index.
      if (index === rowIndex) {
        return !item;
      }
      return item;
    });
    // Update the state with the new array.
    setExpandedState(newExpandedState);
  };

  return (
    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', height: '100%', width: '100%' }}>
      <Box sx={{ flex: '0 0 auto' }}>
        <EnhancedTableToolbar
          title={title}
          editable={editable}
          searchable={searchable}
          deletable={deletable}
          dateRange={dateRange}
          addNewElement={addNewElement}
          allowsRefresh={allowsRefresh}
          iconButton={iconButton}
          search={search}
          selected={selected}
          filters={filters}
          actions={actions}
          startDate={startDate}
          endDate={endDate}
          onRefreshClick={loadData}
          onSearch={handleSearch}
          onApplyFilters={handleApplyFilters}
          onDateRangeChange={onDateRangeChange}
          onDelete={handleDelete}
        />
      </Box>
      {isFetching ? (
        <Box sx={{ position: 'relative', height: '100%', width: '100%', p: 2, flexGrow: 1, overflow: 'auto' }}>
          <CircularProgress
            sx={{
              position: 'absolute',
              marginLeft: 'auto',
              marginRight: 'auto',
              top: '50%',
              left: 0,
              right: 0,
              textAlign: 'center',
            }}
          />
          <Table>
            <TableBody>
              {Array.from(new Array(10)).map((_, index) => (
                <TableRow key={`skeleton-${index}-row`}>
                  {Array.from(new Array(5)).map((_, index) => (
                    <TableCell key={`skeleton-${index}-row-cell`} variant="body">
                      <Skeleton variant="rectangular" height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
          <Table stickyHeader aria-labelledby="tableTitle" size={isDesktop ? 'small' : 'medium'} {...tableProps}>
            <EnhancedTableHead
              editable={editable}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              rowCount={tableData.length || 0}
              columns={isGrouped && groupedColumns != null ? groupedColumns : columns}
              showActions={editable}
              showExpandColumn={isGrouped || (expandable && getDetailRow)}
              showCheckBoxColumn={selectable && showCheckBoxColumn}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {data.length > 0 ? (
                tableData.map((row, rowIndex) => {
                  const isItemSelected = isSelected(row.id);
                  const DynamicTableRow = isGrouped ? DynamicRow : TableRow;

                  return isGrouped ? (
                    <DynamicRow
                      key={`grouped-${rowIndex}-${row.id}-row`}
                      order={order}
                      orderBy={orderBy}
                      onClick={onRowClick != null ? () => onRowClick(row) : null}
                      onRequestSort={handleRequestSort}
                      rowDetailColumns={columns}
                      rowDetailRenderer={() =>
                        row.data.map((groupedRow, groupedRowIndex) => {
                          const isGroupedItemSelected = isSelected(groupedRow.id);
                          return (
                            <Fragment
                              key={`grouped-${rowIndex}-${row.id}-row-${groupedRow.id}-${groupedRowIndex}-detail`}
                            >
                              <DynamicTableRow
                                selected={isGroupedItemSelected}
                                hover={hoverable}
                                role="checkbox"
                                aria-checked={isGroupedItemSelected}
                                tabIndex={-1}
                                data-id={groupedRow.id}
                              >
                                {selectable && showCheckBoxColumn && (
                                  <TableCell padding="checkbox" variant="body">
                                    <Checkbox
                                      color="primary"
                                      checked={isGroupedItemSelected}
                                      onClick={event => handleClick(event, groupedRow.id, true)}
                                      inputProps={{
                                        'aria-labelledby': `enhanced-table-checkbox-${groupedRowIndex}`,
                                      }}
                                    />
                                  </TableCell>
                                )}
                                {columns.map((column, columnIndex) => {
                                  let value = groupedRow[column.id] || '';

                                  if (typeof column.cellRenderer === 'function') {
                                    value = column.cellRenderer(value, groupedRow, groupedRowIndex);
                                  }

                                  return (
                                    <TableCell
                                      key={`grouped-${rowIndex}-${row.id}-row-${groupedRow.id}-${groupedRowIndex}-detail-${columnIndex}-${column.id}-row-cell`}
                                      variant="body"
                                      align={column.align ?? 'left'}
                                      onClick={event => {
                                        if (hoverable && selectable) {
                                          handleClick(event, groupedRow.id, false);
                                        }
                                      }}
                                    >
                                      {value}
                                    </TableCell>
                                  );
                                })}
                                {editable && onEdit && (
                                  <TableCell align="right" variant="body">
                                    <IconButton onClick={() => onEdit(groupedRow)}>
                                      <EditIcon />
                                    </IconButton>
                                    {deletable && onDelete && (
                                      <IconButton onClick={() => handleDelete([groupedRow.id])}>
                                        <Delete />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                )}
                              </DynamicTableRow>
                            </Fragment>
                          );
                        })
                      }
                    >
                      {groupedColumns.map((column, columnIndex) => {
                        let value = row[column.id] || '';

                        if (typeof column.cellRenderer === 'function') {
                          value = column.cellRenderer(value, row, rowIndex);
                        }

                        return (
                          <TableCell
                            key={`grouped-${rowIndex}-${row.id}-${rowIndex}-${columnIndex}-cell`}
                            align={column.align ?? 'left'}
                            onClick={event => {
                              if (hoverable && selectable) {
                                handleClick(event, row.id, false);
                              }
                            }}
                          >
                            {value}
                          </TableCell>
                        );
                      })}
                    </DynamicRow>
                  ) : (
                    <Fragment>
                      <DynamicTableRow
                        key={`ungrouped-${rowIndex}-${row.id}`}
                        selected={isItemSelected}
                        hover={hoverable}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        data-id={row.id}
                        onClick={onRowClick != null ? () => onRowClick(row) : null}
                      >
                        {selectable && showCheckBoxColumn && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              onClick={event => handleClick(event, row.id, true)}
                              inputProps={{
                                'aria-labelledby': `enhanced-table-checkbox-${rowIndex}`,
                              }}
                            />
                          </TableCell>
                        )}
                        {columns.map((column, columnIndex) => {
                          let value = row[column.id] || '';

                          if (typeof column.cellRenderer === 'function') {
                            value = column.cellRenderer(value, row, rowIndex);
                          }

                          return (
                            <TableCell
                              key={`ungrouped-${rowIndex}-${row.id}-${columnIndex}-${column.id}-cell`}
                              align={column.align ?? 'left'}
                              onClick={event => {
                                if (hoverable && selectable) {
                                  handleClick(event, row.id, false);
                                }
                              }}
                            >
                              {value}
                            </TableCell>
                          );
                        })}
                        {editable && onEdit && (
                          <TableCell align="right">
                            <IconButton onClick={() => onEdit(row)}>{editIcon}</IconButton>
                            {deletable && onDelete && (
                              <IconButton onClick={() => handleDelete([row.id])}>
                                <Delete />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                        {expandable && getDetailRow && (
                          <TableCell align="right">
                            <IconButton onClick={() => toggleExpandRow(rowIndex)}>
                              <ExpandMore />
                            </IconButton>
                          </TableCell>
                        )}
                      </DynamicTableRow>
                      {expandedState[rowIndex] && getDetailRow(row)}
                    </Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0) + (editable && onEdit ? 1 : 0)}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        p: 8,
                      }}
                    >
                      <SearchOff fontSize="large" sx={{ mb: 2 }} />
                      <Typography variant="h7">{t('general.noResultsMessage')}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {footerRow}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {data.length > 0 && !isFetching && (
        <TablePagination
          sx={{
            '& p': {
              mt: 'unset',
              mb: 'unset',
            },
            overflow: 'unset',
            '&>.MuiToolbar-root': {
              display: 'flex',
              justifyContent: { xs: 'center', md: 'end' }, // Ensures pagination is centered within its container
              flexWrap: 'wrap', // Allows items to wrap within the container on smaller screens
            },
          }}
          showFirstButton
          showLastButton
          component="div"
          count={totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page ?? 0}
          rowsPerPageOptions={rowsPerPageOptions}
          labelRowsPerPage={t('general.rowsPerPage')}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
}

const EnhancedTable = forwardRef(DynamicTable);

EnhancedTable.propTypes = {
  title: PropTypes.string,
  rows: PropTypes.array,
  columns: PropTypes.arrayOf(PropTypes.object),
  groupedColumns: PropTypes.arrayOf(PropTypes.object),
  getDetailRow: PropTypes.func,
  editable: PropTypes.bool,
  clickable: PropTypes.bool,
  selectable: PropTypes.bool,
  showCheckBoxColumn: PropTypes.bool,
  hoverable: PropTypes.bool,
  searchable: PropTypes.bool,
  deletable: PropTypes.bool,
  expandable: PropTypes.bool,
  dateRange: PropTypes.bool,
  allowsRefresh: PropTypes.bool,
  fetchData: PropTypes.func,
  editIcon: PropTypes.element,
  filters: PropTypes.arrayOf(PropTypes.object),
  initialPage: PropTypes.number,
  initialSearch: PropTypes.string,
  initialSelected: PropTypes.arrayOf(PropTypes.number),
  searchFields: PropTypes.arrayOf(PropTypes.string),
  defaultOrder: PropTypes.string,
  defaultOrderBy: PropTypes.string,
  defaultRowsPerPage: PropTypes.number,
  addNewElement: PropTypes.element,
  actions: PropTypes.arrayOf(PropTypes.element),
  footerRow: PropTypes.element,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  dateKeyword: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onPageChange: PropTypes.func,
  onSelectedChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onSearch: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onDateRangeChange: PropTypes.func,
  onActiveFiltersChange: PropTypes.func,
  onDataChange: PropTypes.func,
};

export default EnhancedTable;
